/* eslint-disable camelcase */
import { Button } from "@nextui-org/button";
import React, { useEffect, useRef, useState } from "react";
import { apiService } from "../services/apiService";
import toast from "react-hot-toast";

interface ModalGiveFeedbackProps {
  onClose: () => void;
  msg_id: number;
  user_id: number;
  chat_id: number;
  buttonId: string;
}

const ModalGiveFeedback: React.FC<ModalGiveFeedbackProps> = ({
  onClose,
  msg_id,
  user_id,
  chat_id,
  buttonId,
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  // Trigger animation on modal open
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Custom outside click handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        overlayRef.current &&
        !modalRef.current?.contains(e.target as Node) &&
        overlayRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Prevent closing modal on inner clicks
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Handle save button click
  const handleSave = async () => {
    if (inputValue.trim() || description) {
      try {
        setLoading(true);
        const comments = description !== ''
          ? `${inputValue.trim()} ${description}`
          : inputValue.trim();

        const feedbackData = {
          chat_id,
          msg_id,
          user_id,
          remarks: buttonId === "good" ? 'Good Response' : "Feedback",
          comments,
          payload: {
            rating: 5,
            tags: ["user feedback"],
          },
        };
        await apiService.postFeedback(feedbackData);
        setLoading(false);
        toast.success("Feedback Given", {
          className: "md:!bg-[#00B21B]/20 !bg-[#00B21B]/100 dark:!text-white dark:text-[#191919] !shadow-none",
          duration: 2000,
        });
        // Close modal after successful feedback
        onClose();
      } catch (error) {
        setLoading(false);
        toast.error("Error submitting feedback", {
          className: "md:!bg-[#FD7272]/20 !bg-[#FD7272]/100 dark:!text-white dark:text-[#191919] !shadow-none",
          duration: 2000,
        });
      }
    } else {
      toast.error("Please enter your feedback.", {
        className: "md:!bg-[#FD7272]/20 !bg-[#FD7272]/100 dark:!text-white dark:text-[#191919] !shadow-none",
        duration: 2000,
      });
    }
  };
  const preValues = [
    "Wrong Answer",
    "Factually incorrect",
    "Did not answer",
    "Wrong Reference Document(s)",
    "Did not follow instructions",
    "Confusing Response",
    "Not enough details",
    "Too much detail",
    "Other",
  ];

  return (
    <div
      aria-hidden="true"
      className="fixed md:backdrop-blur-[auto] backdrop-blur-10 inset-0 flex justify-center items-center z-50"
      ref={overlayRef}
    >
      <div
        aria-hidden="true"
        className={`${isVisible ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"} dark:bg-chatDarkBg/70 bg-lightBg/50 border-[#5F829F]/50 md:border-0 border-[0.2px] brightness-[94%] rounded-3xl max-w-[33.5rem] w-full relative backdrop-blur-[42px] dark:brightness-[94%] md:p-7 p-5`}
        ref={modalRef}
        onClick={handleModalClick}
      >
        <h2 className={`text-base tracking-wide text-black dark:text-white font-proximaBold ${buttonId === "good" && "pb-6"}`}>
          Please give your feedback
        </h2>
        {buttonId === "bad" && <div className="flex flex-wrap items-start justify-start gap-3 py-6">
          {preValues.map((item, index) => (
            <button
              key={index + 1}
              onClick={() => setInputValue(`${item}: `)}
              className={`select-none backdrop-blur-[42px] py-2 text-sm md:text-base sm:px-4 px-2 rounded-[9px] w-fit border-[#5f829f73] border-[0.2px] text-black dark:text-white font-ProximaLight transition-colors duration-300 ease-soft-spring ${inputValue === `${item}: ` ? "dark:!bg-[#5F829F] !dark:brightness-100 !brightness-110 !bg-[#b2c1cc] dark:text-white" : ""}`}
            >
              {item}
            </button>
          ))}
        </div>}
        <div className="flex flex-col gap-5">
          <textarea
            cols={10}
            rows={3}
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="dark:bg-white/5 bg-white/15 rounded-lg border-[#5F829F]/30 brightness-[103%] backdrop-blur-[42px] border-[0.2px] focus:outline-none resize-none w-full px-3.5 py-3 dark:text-white text-sm dark:placeholder:text-[#5E829F]"
            placeholder="Feel free to to add specific details (optional)"
          />
          <div className="flex justify-end gap-3">
            <Button
              onPress={onClose}
              className="py-3 rounded-lg bg-transparent px-5 w-fit hover:text-white border-[#707070] hover:bg-[#707070] border-[0.2px] text-black dark:text-white text-sm md:text-base font-ProximaLight"
            >
              Cancel
            </Button>
            <Button
              onPress={handleSave}
              className="backdrop-blur-[42px] py-3 rounded-lg bg-[#265376] hover:bg-transparent dark:hover:text-white hover:text-[#265376] px-5 w-fit border-[#265376] border-[0.2px] text-white text-sm md:text-base font-ProximaLight"
            >
              {loading ? <span className="block mx-auto loader-dots"></span> : "Submit"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalGiveFeedback;
